import * as React from "react"
import {Box, Grid, Typography, Chip} from "@mui/material";
import {AppLink} from "@ui-seeker/app";
import {ButtonLoadingSeeker, IconSeeker, ImageSeeker} from "@ui-seeker/core";
import {FORMAT_DATE_BACK, formatDate, formatPrice, getCurrentDate} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";
import {reverseMatcherImageComparatorModalData} from "../@signals";
import {useProxiedImage} from "../../@hooks";


const currentDateString = formatDate(getCurrentDate(), { format: FORMAT_DATE_BACK })

// Función para determinar el color del badge según el score
const getScoreColor = (distance) => {
	const score = parseFloat(distance)
	if (isNaN(score)) return 'default'
	
	if (score >= 0.9) return 'success'      // Verde - Excelente match
	if (score >= 0.7) return 'primary'      // Azul - Buen match
	if (score >= 0.5) return 'warning'      // Naranja - Match medio
	return 'error'                          // Rojo - Match bajo
}

export default function ReverseMatcherTableRow({ item, onValidate, onEdit, onNotFound, isValidating }) {
	const { skuID, skuName, ean, brand, line, reference, retailer, country, siteID, aiCandidate } = item
	const hasCandidate = aiCandidate !== null
	const { decodedURL: candidateThumbnail } = useProxiedImage(aiCandidate?.thumbnail)
	
	// Construir nombre completo del candidato con variant
	const getCandidateName = () => {
		if (!aiCandidate) return 'No Candidate'
		const parts = [
			aiCandidate.superTitle,
			aiCandidate.productTitle,
			aiCandidate.subtitle,
			aiCandidate.variant
		].filter(Boolean)
		return parts.join(' ')
	}
	const candidateName = getCandidateName()

	return <Grid container 
		spacing={2} 
		p={2} 
		mb={2}
		sx={{ 
			border: '2px solid',
			borderColor: hasCandidate ? 'divider' : 'warning.light',
			borderRadius: 1,
			backgroundColor: 'background.paper'
		}}
	>
		{/* SKU Section - Left */}
		<Grid item xs={6}>
			<Box display="flex" flexDirection="column" gap={1}>
				<Box display="flex" gap={2} alignItems="flex-start">
					<ImageSeeker
						useSpinner
						lazyLoad={false}
						src={`${BACKEND_APIS.BACKEND}/sku/image/${skuID}`}
						sx={{ 
							border: '1px solid', 
							borderColor: 'divider', 
							width: 120, 
							height: 'auto',
							maxHeight: 150,
							cursor: 'pointer',
							borderRadius: 1
						}}
						onClick={() => reverseMatcherImageComparatorModalData.value = {
							title: `${skuName} vs ${candidateName}`,
							fullUrlLeft: `${BACKEND_APIS.BACKEND}/sku/image/${skuID}`,
							urlRight: candidateThumbnail,
							skuName: skuName,
							candidateName: candidateName,
							targetIndex: 0
						}}
					/>
					
					<Box flex={1}>
						<Typography fontWeight="bold" fontSize="0.95rem" mb={0.5}>
							{skuName}
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							<strong>SKU ID:</strong> {skuID}
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							<strong>EAN:</strong> {ean || "-"}
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							<strong>Brand:</strong> {brand || "-"}
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							<strong>Line:</strong> {line || "-"}
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							<strong>Reference:</strong> {reference || "-"}
						</Typography>
					</Box>
				</Box>
			</Box>
		</Grid>

		{/* AI Candidate Section - Right */}
		<Grid item xs={6}>
			{hasCandidate ? (
				<Box display="flex" flexDirection="column" gap={1} height="100%">
					{aiCandidate.alreadyMapped && (
						<Box 
							display="flex" 
							alignItems="center" 
							gap={1} 
							bgcolor="warning.light" 
							borderRadius={1} 
							p={1}
							sx={{ opacity: 0.9 }}
						>
							<IconSeeker icon="triangle-exclamation" sx={{ color: 'warning.dark', fontSize: "1rem" }} />
							<Typography fontWeight="bold" fontSize="0.85rem" color="warning.dark">
								Already Mapped to SKU: {aiCandidate.skuNameMapped || aiCandidate.idSkuMapped} ({aiCandidate.idSkuMapped})
							</Typography>
						</Box>
					)}
					
					<Box display="flex" gap={2} alignItems="flex-start" flex={1}>
						<Box position="relative">
							<ImageSeeker
								useSpinner
								lazyLoad={false}
								src={aiCandidate.thumbnail}
								sx={{ 
									border: '1px solid', 
									borderColor: 'divider', 
									width: 120, 
									height: 'auto',
									maxHeight: 150,
									cursor: 'pointer',
									borderRadius: 1
								}}
								onClick={() => reverseMatcherImageComparatorModalData.value = {
									title: `${skuName} vs ${candidateName}`,
									fullUrlLeft: `${BACKEND_APIS.BACKEND}/sku/image/${skuID}`,
									urlRight: candidateThumbnail,
									skuName: skuName,
									candidateName: candidateName,
									targetIndex: 0
								}}
							/>
							<Box position="absolute" top={-8} right={-8}>
								<Chip
									icon={<IconSeeker icon="sparkles" sx={{ fontSize: "0.7rem !important", color: "inherit !important" }} />}
									label={aiCandidate.AIDistance ? `${Math.round(parseFloat(aiCandidate.AIDistance) * 100)}%` : "N/A"}
									color={getScoreColor(aiCandidate.AIDistance)}
									variant="filled"
									size="small"
									sx={{ fontSize: "0.7rem", height: 20, fontWeight: 'bold' }}
								/>
							</Box>
						</Box>
						
						<Box flex={1}>
							{aiCandidate.superTitle && (
								<Typography fontSize="0.85rem" color="text.disabled">
									{aiCandidate.superTitle}
								</Typography>
							)}
							<Box display="flex" alignItems="center" gap={1} mb={0.5}>
								<AppLink 
									to={`${BACKEND_APIS.BACKEND}/sku/product-url/${siteID}/${aiCandidate.productID}/${currentDateString}`} 
									target="_blank"
								>
									<Typography display="flex" alignItems="center" gap={0.5} fontWeight="bold" fontSize="0.95rem">
										{aiCandidate.productTitle} {aiCandidate.variant && `${aiCandidate.variant}`}
										<IconSeeker icon="external-link" sx={{ color: 'text.disabled', fontSize: "0.75rem" }} />
									</Typography>
								</AppLink>
								<Chip 
									label={retailer} 
									size="small" 
									color="info"
									sx={{ fontSize: "0.7rem", height: 20 }}
								/>
							</Box>
							{aiCandidate.subtitle && (
								<Typography fontSize="0.85rem" color="text.disabled">
									{aiCandidate.subtitle}
								</Typography>
							)}
							<Typography fontSize="0.85rem" color="text.secondary" mt={0.5}>
								<strong>Product ID:</strong> {aiCandidate.productID}
							</Typography>
							<Typography fontSize="0.85rem" color="text.secondary">
								<strong>Variant:</strong> {aiCandidate.variant || "-"}
							</Typography>
							<Typography fontSize="0.85rem" color="text.secondary">
								<strong>Price:</strong> {formatPrice(aiCandidate.actualPrice, aiCandidate.currency)}
							</Typography>
							<Typography fontSize="0.85rem" color="text.secondary">
								<strong>End Date:</strong> {formatDate(aiCandidate.date, { isString: true })}
							</Typography>
						</Box>

						{/* Action Buttons */}
						<Box display="flex" flexDirection="column" gap={1} justifyContent="flex-end" minWidth={140}>
							<ButtonLoadingSeeker
								color="success"
								size="small"
								loading={isValidating}
								disabled={!hasCandidate}
								startIcon={<IconSeeker icon="badge-check" />}
								onClick={() => onValidate(item)}
								sx={{ width: '100%' }}
							>
								Validate
							</ButtonLoadingSeeker>

							<ButtonLoadingSeeker
								color="error"
								size="small"
								loading={isValidating}
								startIcon={<IconSeeker icon="person-circle-question" />}
								onClick={() => onNotFound(item)}
								sx={{ width: '100%' }}
							>
								Not Found
							</ButtonLoadingSeeker>

							<ButtonLoadingSeeker
								color="primary"
								size="small"
								startIcon={<IconSeeker icon="pen-to-square" />}
								onClick={() => onEdit(item)}
								sx={{ width: '100%' }}
							>
								Edit
							</ButtonLoadingSeeker>
						</Box>
					</Box>
				</Box>
			) : (
				<Box 
					display="flex" 
					flexDirection="column" 
					justifyContent="center" 
					alignItems="center" 
					height="100%"
					gap={2}
				>
					<IconSeeker icon="circle-xmark" sx={{ fontSize: "3rem", color: 'text.disabled' }} />
					<Typography variant="h6" color="text.disabled">
						No AI Candidate Available
					</Typography>
					<Box display="flex" gap={1}>
						<ButtonLoadingSeeker
							color="error"
							size="small"
							loading={isValidating}
							startIcon={<IconSeeker icon="person-circle-question" />}
							onClick={() => onNotFound(item)}
						>
							Not Found
						</ButtonLoadingSeeker>
						<ButtonLoadingSeeker
							color="primary"
							size="small"
							startIcon={<IconSeeker icon="pen-to-square" />}
							onClick={() => onEdit(item)}
						>
							Edit
						</ButtonLoadingSeeker>
					</Box>
				</Box>
			)}
		</Grid>
	</Grid>
}
